import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useAlerts, userAgent, Text } from '@theneatcompany/nui'
import { useReCaptcha } from 'next-recaptcha-v3'
import { useRouter } from 'next/router'
import { useIntl } from 'react-intl'
import { usePathname } from 'next/navigation'

import { sendError, trackSignUpStarted } from '@/utils/rollbar'
import Spinner from '@/components/common/FullScreenSpinner'
import Authenticate from '@/components/Pages/FreeTrial/Authenticate'
import NewUser from '@/components/Pages/FreeTrial/NewUser'
import ExistingUser from '@/components/Pages/FreeTrial/ExistingUser'
import { extractMessage } from '@/utils/errors'
import { WEB_APP_URL, APP_URL, AUTH_URL } from '@/config'
import { RECAPTCHA_V3_ACTIONS, ORDER_SOURCE } from '@/constants'
import { handleVerifyRecaptchaV3 } from '@/utils/recaptcha'
import { getVariantCookies } from '@/lib/tracking/success'
import WebAppLink from '@/components/common/WebAppLink'

const STEPS = {
  authenticate: 'authenticate',
  existingUser: 'existingUser',
  resetPassword: 'resetPassword',
  newUser: 'newUser',
}

const TRIAL_PAGES = [
  '/free-trial',
  '/track-receipts-trial',
]

const EXPIRED = 'expired'

const FreeTrialForm = ({ source, planSku }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isValidating, setIsValidating] = useState(false)
  const [formValues, setFormValues] = useState({})
  const [navigation, setNavigation] = useState({ steps: STEPS, currentStep: STEPS.authenticate })
  const [user, setUser] = useState({})

  const { executeRecaptcha, loaded } = useReCaptcha()
  const pathname = usePathname()
  const { formatMessage } = useIntl()
  const alerts = useAlerts()

  const navigateToStep = (step) => {
    setNavigation({ ...navigation, ...{ currentStep: navigation.steps[step] } })
  }

  const {
    query: {
      utm_source: utmSource,
      utm_medium: utmMedium,
      utm_campaign: utmCampaign,
      couponCode = '',
      exists = false,
      jwt = '',
      oauthToken = '',
      refreshJWT = '',
      jwtExpiresAt = '',
      refreshJWTExpiresAt = '',
    },
    isReady,
  } = useRouter()

  const AUTH_RETURN_URL = `${WEB_APP_URL}/auth-return?jwt=${jwt}&oauthToken=${oauthToken}&refreshJWT=${refreshJWT}&jwtExpiresAt=${jwtExpiresAt}&refreshJWTExpiresAt=${refreshJWTExpiresAt}`
  const AUTH_CALLBACK_URL = encodeURIComponent(`${APP_URL}/${pathname}?sku=${planSku}${couponCode ? `&couponCode=${couponCode}` : ''}${utmSource ? `&utm_source=${utmSource}` : ''}${utmMedium ? `&utm_medium=${utmMedium}` : ''}${utmCampaign ? `&utm_campaign=${utmCampaign}` : ''}&exists=true`)
  const AUTH_REDIRECT_URL = `${AUTH_URL}?scope=NEAT_COM&callbackUrl=${AUTH_CALLBACK_URL}&tokenVersion=3`

  const handleRedirectToApp = (order) => {
    if (jwt) {
      window.location.assign(AUTH_RETURN_URL)
    } else if (order?.ssoUrl) {
      window.location.assign(order.ssoUrl)
    } else {
      window.location.assign(APP_URL)
    }
  }

  const handleValidate = async ({ email = null, googleId = null }) => {
    setIsValidating(true)
    const order = {
      user: {
        ...(email && { email }),
        ...(googleId && { googleId }),
        ...(oauthToken && { oauthToken }),
      },
      source,
    }

    try {
      const token = await executeRecaptcha(RECAPTCHA_V3_ACTIONS.FREE_TRIAL_EMAIL)
      await handleVerifyRecaptchaV3(token, { email: 'test@test.com' })
      const res = await fetch('/api/signup/validate', {
        method: 'POST',
        body: JSON.stringify(order),
      })

      const resMessage = await res.json()

      if (resMessage?.account?.subscriptions.some(sub => sub.planSku === planSku && sub.status !== EXPIRED)) {
        resMessage.status = 0
      }

      setUser(resMessage.user)
      return resMessage
    } catch (err) {
      sendError(err, 'Error fetching /api/signup/validate')
      alerts.error(extractMessage(err))
      return { status: 0 }
    } finally {
      setIsValidating(false)
    }
  }

  useEffect(() => {
    const el = document.querySelector('html')
    if (el) el.scrollTop = 0
  }, [navigation.currentStep])

  const handleNoCcAiVipError = () => alerts.error(
    <Text>
      It looks like you already have access to all the features of Neat +
      Automated Insights, <br />
      <WebAppLink
        eventTrackingProperties={{
          location: 'Free Trial Form NoCCAiVipError alert',
        }}
      >
        sign-in to your account
      </WebAppLink>{' '}
      to get started.
    </Text>,
  )

  const saveAuthenticateData = async (model) => {
    const validation = await handleValidate(model)

    const nextFormValues = (
      {
        ...formValues,
        ...model,
        userId: validation.user?.neatUserId,
        firstName: validation.user?.firstName,
        lastName: validation.user?.lastName,
      }
    )
    setFormValues(nextFormValues)

    trackSignUpStarted({
      ...validation.user,
      email: model.email,
    })

    if (validation.error) {
      alerts.error(validation.error.message ? validation.error.message : formatMessage({ id: 'pages.signUp.unexpectedError' }))
    } else if (source === ORDER_SOURCE.NO_CC_AI_VIP && validation.status === 0) {
      handleNoCcAiVipError()
    } else if (validation.user?.neatUserId) {
      window.location.assign(AUTH_REDIRECT_URL)
    } else if (validation.status === 1) {
      navigateToStep(navigation.steps.newUser)
    } else {
      alerts.error(formatMessage({ id: 'pages.signUp.unexpectedError' }))
    }
  }

  const handleGoogleAuth = async (response = {}) => {
    const { credential } = response
    let parsedCredentials = null

    try {
      parsedCredentials = JSON.parse(credential.split('.').map(part => Buffer.from(part, 'base64').toString())[1])
    } catch (error) {
      alerts.error('There was an error validating your Google Account. Please try again.')
      return
    }

    const { email, family_name: lastName, given_name: firstName } = parsedCredentials

    const nextValues = { ...formValues, email, firstName, lastName, googleId: credential }
    setFormValues(nextValues)
    const validation = await handleValidate({ googleId: credential })

    trackSignUpStarted({
      email,
      firstName: validation.user.firstName || firstName,
      lastName: validation.user.lastName || lastName,
      neatUserId: validation.user.neatUserId,
    })

    const userId = validation.user?.neatUserId

    // Check if user exists in Staypuft
    if (source === ORDER_SOURCE.NO_CC_AI_VIP && validation.status === 0) {
      handleNoCcAiVipError()
    } else if (userId) {
      setFormValues({ ...nextValues, userId })
      window.location.assign(AUTH_REDIRECT_URL)
    } else if (validation.status === 1) {
      navigateToStep(navigation.steps.newUser)
    } else {
      alerts.error(formatMessage({ id: 'pages.signUp.unexpectedError' }))
    }
  }

  const createOrder = async (data) => {
    setIsLoading(true)

    try {
      const res = await fetch('/api/signup/createTrial', {
        method: 'POST',
        body: JSON.stringify({
          user: {
            email: data.email,
            firstName: data.firstName,
            lastName: data.lastName,
            phone: data.phone,
            ...(data.neatUserId && { neatUserId: data.neatUserId }),
            ...(data.password && { password: data.password }),
            ...(data.googleId && { googleId: data.googleId }),
          },
          plan: { sku: planSku },
          source,
          device: { os: userAgent.device?.model, type: userAgent.browser?.name },
          utm: { testsShown: getVariantCookies() },
        }),
      })
      const order = await res.json()

      if (res.status >= 300 || order.status > 300) {
        alerts.error(res.message || 'Error creating order. Please try again')
      } else {
        handleRedirectToApp(order)
      }
    } catch (err) {
      alerts.error('There was an error placing the order. Please try again.')
    }
    setIsLoading(false)
  }

  useEffect(() => {
    if (!isReady || !loaded || !TRIAL_PAGES.includes(pathname)) {
      if (jwt) {
        setIsValidating(true)
      } else {
        setIsValidating(false)
      }
      return
    }

    const handleExistingUser = async () => {
      if (exists && oauthToken) {
        const { status, user: { neatUserId } } = await handleValidate({})

        if (status === 0) {
          navigateToStep(navigation.steps.existingUser)
          setTimeout(() => {
            handleRedirectToApp()
          }, 10000)
        } else {
          await createOrder({ ...formValues, neatUserId })
        }
      }
    }

    handleExistingUser()
  }, [isReady, loaded])

  return (
    <>
      {
        {
          authenticate: <Authenticate
            handleGoogleAuth={handleGoogleAuth}
            saveAuthenticateData={saveAuthenticateData}
          />,
          existingUser: <ExistingUser
            handleRedirectToApp={handleRedirectToApp}
            user={user}
          />,
          newUser: <NewUser createOrder={createOrder} formValues={formValues} />,
        }[navigation.currentStep]
      }

      {isLoading &&
        <Spinner><h2>Setting Up Your Account</h2>
          <p>Please wait...</p>
        </Spinner>}
      {isValidating &&
        <Spinner><h2>Verifying</h2>
          <p>Please wait...</p>
        </Spinner>}
    </>
  )
}

FreeTrialForm.propTypes = {
  source: PropTypes.string.isRequired,
  planSku: PropTypes.string.isRequired,
}

export default FreeTrialForm
